(() => {
  'use strict';

  const SELECTOR = '[data-minima-product-search]';
  const MIN_QUERY_LENGTH = 2;
  const DEBOUNCE_MS = 180;
  const MAX_RESULTS = 6;

  class MinimaProductSearch {
    constructor(root, index) {
      this.root = root;
      this.index = index;
      this.form = root.querySelector('form');
      this.input = root.querySelector('[data-minima-search-input]');
      this.results = root.querySelector('[data-minima-search-results]');
      this.status = root.querySelector('[data-minima-search-status]');
      this.submitButton = root.querySelector('[data-minima-search-submit]');
      this.endpoint = this.normaliseEndpoint(root.dataset.searchEndpoint || '');
      this.searchUrl = root.dataset.searchUrl || '/search';
      this.currency = root.dataset.currency || 'EUR';
      this.locale = root.dataset.locale || document.documentElement.lang || 'es-ES';
      this.timer = null;
      this.controller = null;
      this.cache = new Map();
      this.options = [];
      this.activeIndex = -1;
      this.lastQuery = '';

      if (!this.form || !this.input || !this.results || !this.endpoint) return;

      this.results.id ||= `MinimaProductSearchResults-${index}`;
      this.input.setAttribute('aria-controls', this.results.id);
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.results.setAttribute('role', 'listbox');

      this.bindEvents();
    }

    normaliseEndpoint(value) {
      if (value) {
        return value.endsWith('.json') ? value : `${value.replace(/\/$/, '')}.json`;
      }

      const root = window.Shopify?.routes?.root || '/';
      return `${root.replace(/\/?$/, '/')}search/suggest.json`;
    }

    bindEvents() {
      this.input.disabled = false;
      this.input.readOnly = false;
      this.input.autocomplete = 'off';

      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('focus', () => {
        const query = this.getQuery();
        if (query.length >= MIN_QUERY_LENGTH && this.results.childElementCount > 0) this.open();
      });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.form.addEventListener('submit', (event) => this.onSubmit(event));

      document.addEventListener('pointerdown', (event) => {
        if (!this.root.contains(event.target)) this.close();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen()) {
          this.close();
          this.input.focus();
        }
      });
    }

    getQuery() {
      return this.input.value.trim();
    }

    onInput() {
      const query = this.getQuery();
      window.clearTimeout(this.timer);
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');

      if (query.length < MIN_QUERY_LENGTH) {
        this.lastQuery = query;
        this.abortRequest();
        this.clear();
        this.close();
        this.setStatus(query.length === 1 ? 'Escribe al menos 2 caracteres.' : '');
        return;
      }

      if (query === this.lastQuery && this.results.childElementCount > 0) {
        this.open();
        return;
      }

      this.lastQuery = query;
      this.setLoading(true);
      this.timer = window.setTimeout(() => this.load(query), DEBOUNCE_MS);
    }

    async load(query) {
      const cacheKey = query.toLocaleLowerCase(this.locale);
      if (this.cache.has(cacheKey)) {
        this.render(this.cache.get(cacheKey), query);
        return;
      }

      this.abortRequest();
      this.controller = new AbortController();

      const url = new URL(this.endpoint, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', String(MAX_RESULTS));
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set(
        'resources[options][fields]',
        'title,product_type,variants.title,vendor'
      );

      try {
        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: { Accept: 'application/json' },
          signal: this.controller.signal,
        });

        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

        const data = await response.json();
        const products = data?.resources?.results?.products || [];
        this.cache.set(cacheKey, products);

        if (this.getQuery() === query) this.render(products, query);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[Minima product search]', error);
        if (this.getQuery() === query) this.renderError();
      } finally {
        if (this.getQuery() === query) this.setLoading(false);
      }
    }

    abortRequest() {
      if (this.controller) {
        this.controller.abort();
        this.controller = null;
      }
    }

    render(products, query) {
      this.clear();

      const panel = document.createElement('div');
      panel.className = 'minima-product-search__panel';

      const heading = document.createElement('div');
      heading.className = 'minima-product-search__heading';
      heading.textContent = products.length > 0 ? 'Productos sugeridos' : 'Sin coincidencias';
      panel.appendChild(heading);

      if (products.length > 0) {
        const list = document.createElement('div');
        list.className = 'minima-product-search__list';

        products.forEach((product, productIndex) => {
          list.appendChild(this.createProductOption(product, productIndex));
        });

        panel.appendChild(list);
      } else {
        const empty = document.createElement('p');
        empty.className = 'minima-product-search__empty';
        empty.textContent = `No encontramos productos para “${query}”.`;
        panel.appendChild(empty);
      }

      const viewAll = document.createElement('a');
      viewAll.className = 'minima-product-search__view-all';
      viewAll.href = this.buildSearchUrl(query);
      viewAll.textContent = products.length > 0
        ? `Ver todos los resultados para “${query}”`
        : 'Buscar en todos los productos';
      viewAll.setAttribute('role', 'option');
      viewAll.id = `${this.results.id}-all`;
      panel.appendChild(viewAll);

      this.results.appendChild(panel);
      this.refreshOptions();
      this.open();
      this.setStatus(
        products.length === 1
          ? '1 producto sugerido.'
          : `${products.length} productos sugeridos.`
      );
    }

    createProductOption(product, productIndex) {
      const link = document.createElement('a');
      link.className = 'minima-product-search__item';
      link.href = product.url;
      link.setAttribute('role', 'option');
      link.setAttribute('aria-selected', 'false');
      link.id = `${this.results.id}-option-${productIndex}`;

      const media = document.createElement('span');
      media.className = 'minima-product-search__media';

      const imageUrl = this.getImageUrl(product);
      if (imageUrl) {
        const image = document.createElement('img');
        image.className = 'minima-product-search__image';
        image.src = this.resizeShopifyImage(imageUrl, 144);
        image.alt = '';
        image.width = 72;
        image.height = 72;
        image.loading = 'lazy';
        media.appendChild(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'minima-product-search__image minima-product-search__image--placeholder';
        placeholder.setAttribute('aria-hidden', 'true');
        media.appendChild(placeholder);
      }

      link.appendChild(media);

      const content = document.createElement('span');
      content.className = 'minima-product-search__content';

      if (product.product_type) {
        const type = document.createElement('span');
        type.className = 'minima-product-search__type';
        type.textContent = product.product_type;
        content.appendChild(type);
      }

      const title = document.createElement('span');
      title.className = 'minima-product-search__title';
      title.textContent = product.title;
      content.appendChild(title);

      const meta = document.createElement('span');
      meta.className = 'minima-product-search__meta';

      const price = document.createElement('span');
      price.className = 'minima-product-search__price';
      price.textContent = this.formatPrice(product.price_min ?? product.price);
      meta.appendChild(price);

      if (product.available === false) {
        const soldOut = document.createElement('span');
        soldOut.className = 'minima-product-search__sold-out';
        soldOut.textContent = 'Agotado';
        meta.appendChild(soldOut);
      }

      content.appendChild(meta);
      link.appendChild(content);

      const arrow = document.createElement('span');
      arrow.className = 'minima-product-search__arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.innerHTML = '<svg viewBox="0 0 24 24"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';
      link.appendChild(arrow);

      return link;
    }

    getImageUrl(product) {
      if (typeof product.image === 'string') return product.image;
      if (typeof product.featured_image === 'string') return product.featured_image;
      if (product.featured_image?.url) return product.featured_image.url;
      if (product.featured_media?.preview_image?.src) return product.featured_media.preview_image.src;
      return '';
    }

    resizeShopifyImage(url, width) {
      try {
        const parsed = new URL(url, window.location.origin);
        parsed.searchParams.set('width', String(width));
        return parsed.toString();
      } catch (_error) {
        return url;
      }
    }

    formatPrice(value) {
      const number = Number.parseFloat(value);
      if (!Number.isFinite(number)) return '';

      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(number);
      } catch (_error) {
        return `${number.toFixed(2)} ${this.currency}`;
      }
    }

    buildSearchUrl(query) {
      const url = new URL(this.searchUrl, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      return `${url.pathname}${url.search}`;
    }

    renderError() {
      this.clear();
      const panel = document.createElement('div');
      panel.className = 'minima-product-search__panel';

      const message = document.createElement('p');
      message.className = 'minima-product-search__empty';
      message.textContent = 'No pudimos cargar las sugerencias. Pulsa Enter para buscar.';
      panel.appendChild(message);
      this.results.appendChild(panel);
      this.open();
      this.setStatus('No se pudieron cargar las sugerencias.');
    }

    onKeydown(event) {
      if (!this.isOpen() && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        if (this.results.childElementCount > 0) this.open();
      }

      if (!this.isOpen()) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.moveSelection(1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.moveSelection(-1);
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        event.preventDefault();
        this.options[this.activeIndex]?.click();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    }

    moveSelection(direction) {
      if (this.options.length === 0) return;

      this.activeIndex = (this.activeIndex + direction + this.options.length) % this.options.length;
      this.options.forEach((option, optionIndex) => {
        const isActive = optionIndex === this.activeIndex;
        option.setAttribute('aria-selected', String(isActive));
        option.classList.toggle('is-active', isActive);
      });

      const activeOption = this.options[this.activeIndex];
      this.input.setAttribute('aria-activedescendant', activeOption.id);
      activeOption.scrollIntoView({ block: 'nearest' });
    }

    onSubmit(event) {
      const query = this.getQuery();
      if (!query) {
        event.preventDefault();
        this.input.focus();
        return;
      }

      if (this.activeIndex >= 0 && this.options[this.activeIndex]) {
        event.preventDefault();
        window.location.assign(this.options[this.activeIndex].href);
      }
    }

    refreshOptions() {
      this.options = Array.from(this.results.querySelectorAll('[role="option"]'));
      this.activeIndex = -1;
    }

    setLoading(isLoading) {
      this.root.classList.toggle('is-loading', isLoading);
      this.input.setAttribute('aria-busy', String(isLoading));
      if (this.submitButton) this.submitButton.classList.toggle('is-loading', isLoading);
      if (isLoading) this.setStatus('Buscando productos…');
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }

    open() {
      this.root.classList.add('is-open');
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.root.classList.remove('is-open');
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;
      this.options.forEach((option) => {
        option.classList.remove('is-active');
        option.setAttribute('aria-selected', 'false');
      });
    }

    clear() {
      this.results.replaceChildren();
      this.options = [];
      this.activeIndex = -1;
    }

    isOpen() {
      return this.root.classList.contains('is-open');
    }
  }

  const initialise = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((root, index) => {
      if (root.dataset.minimaProductSearchReady === 'true') return;
      root.dataset.minimaProductSearchReady = 'true';
      new MinimaProductSearch(root, index + 1);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise());
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
